import React, { ReactElement, useCallback, useState } from "react";

import styled, { createGlobalStyle } from "styled-components";

import { SELECTION_BLUE } from "./elements";
import { WhiteboardPeopleMenu } from "./peopleMenu";
import {
  LIMITED_ACCESS_LABEL,
  ProjectElementDef,
  projectElementTitle,
} from "./projectElements";
import { Button } from "../../components/Button";
import { Chip } from "../../components/Chip";
import { ChooseV2 } from "../../components/ChooseV2";
import { EditableText } from "../../components/EditableText";
import { IconButton } from "../../components/IconButton";
import {
  ArrowLeftIcon,
  AttachmentIcon,
  CaretRightIcon,
  CircleIcon,
  CollapseExpandSingleIcon,
  DependencySmallIcon,
  DrawIcon,
  DuplicateIcon,
  EditIcon,
  FolderMoveIcon,
  FolderPlusIcon,
  InsertLinkIcon,
  LayerBackIcon,
  LayerBackwardIcon,
  LayerForwardIcon,
  LayerFrontIcon,
  LayersIcon,
  LockIcon,
  LockSmallIcon,
  ProjectIcon,
  RoundedRectangleIcon,
  SidebarLeftIcon,
  NoteIcon,
  TaskIcon,
  TextIcon,
  TrashIcon,
  TreeDotsIcon,
  TriangleIcon,
} from "../../components/Icons";
import { List, ListItem, ListSeparator } from "../../components/List";
import { Menu, MenuHeader } from "../../components/Menu";
import { Signifier } from "../../components/Signifier";
import { Tooltip } from "../../components/Tooltip";
import {
  ResizeTransition,
  SlideLeftRightTransition,
} from "../../components/Transitions";
import { Body2, Caption1, Header3 } from "../../components/Typography";

/* ------------------------------------------------------------------ */
/* Outline sidebar                                                     */
/* ------------------------------------------------------------------ */

/* An OUTLINE, not a layers panel. Whiteboard elements are a flat list today
   with no nesting and no names, so the hierarchy here is AUTHORED rather than
   derived from z-order or spatial containment: the user creates groups, names
   things, and drags to order them, and all of that is persisted.
   Deliberately excluded: stacking order. z-order stays on the element context
   menu (send to front / back / forward / backward) and the sidebar never shows
   or edits it — the outline order and the paint order are different questions,
   and conflating them is what makes layers panels confusing on boards that are
   really structured documents.

   The model this prototype stands in for:
     GroupElement   a new element type on the single-table-inheritance
                    `whiteboard_elements` table — no migration needed, and it
                    inherits ops, realtime, undo/redo and trash for free.
                    Element.jsx's render switch already returns null by
                    default, so a group draws nothing on the canvas. It must be
                    filtered out of the spatial index, marquee select and the
                    z-order sort.
     name           a real authored field, separate from `content` (the text
                    painted on the canvas). Needed because the eventual goal is
                    exporting the outline as a document, and `content` is empty
                    on every freshly drawn shape and absent on freehand.
     outlinePosition
                    integer ordering SCOPED PER CONTAINER — groups ordered
                    among themselves, elements ordered within their group.
                    Named to avoid colliding with `element.position`, which is
                    already the {x, y} canvas coordinate. Reordering follows
                    the house TaskReorderer contract: the client sends the full
                    ordered id list, the server renumbers 1..N in one
                    transaction. Here that is a new `reorder` op on the
                    existing ops pipeline, so it inherits undo/redo, the id_map
                    reconcile and the realtime merge.

   ONE level of nesting only. Groups cannot contain groups — an exported
   document does not want arbitrarily deep headings, and multi-level grouping
   is a later, separate model. */

export type OutlineElementType =
  | "circle"
  | "roundedRectangle"
  | "triangle"
  | "postit"
  | "text"
  | "freehand"
  | "link"
  | "file"
  /* The two entity cards. Their literals are the same as the board tools'
     ProjectElementKind, so a card's kind IS its outline type — no mapping. */
  | "task"
  | "project";

/* The local `Glyph` type is for this story's hand-drawn SVGs; the DS icons are
   forwardRef components, so the map needs the wider component type. */
export type OutlineIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

export const OUTLINE_TYPE_ICONS: Record<OutlineElementType, OutlineIcon> = {
  circle: CircleIcon,
  roundedRectangle: RoundedRectangleIcon,
  triangle: TriangleIcon,
  postit: NoteIcon,
  text: TextIcon,
  freehand: DrawIcon,
  link: InsertLinkIcon,
  file: AttachmentIcon,
  task: TaskIcon,
  project: ProjectIcon,
};

/* Fallback label when an element has no name. Deliberately reads as a
   PLACEHOLDER (muted + italic) rather than as a name, because an unnamed
   element exports as nothing — the styling is the warning. */
export const OUTLINE_TYPE_LABELS: Record<OutlineElementType, string> = {
  circle: "Circle",
  roundedRectangle: "Rounded Rectangle",
  triangle: "Triangle",
  postit: "Sticky Note",
  text: "Text",
  freehand: "Freehand",
  link: "Link",
  file: "File",
  task: "Task",
  project: "Project",
};

export interface OutlineElement {
  id: string;
  /* null name = never named; the row shows the type fallback */
  name: string | null;
  type: OutlineElementType;
  locked?: boolean;
  /* How many WhiteboardConnections touch this element. Already derivable today
     from the connections array (start_element_id / end_element_id), so this
     signifier needs no new data — unlike the name and the grouping. Omitted
     entirely at zero rather than shown as "0". */
  connections?: number;
  /* False when the name is not the board's to change: an entity card inherits
     its title from the linked task or project, so the row shows that title and
     never offers to edit it. Same trait, same rule as the Inspector's
     `renamable` (inspector.tsx). Undefined means renamable, so every mock row
     below is unaffected. */
  renamable?: boolean;
  /* The viewer cannot access the linked entity. The row shows the Limited
     Access label instead of a name, and is always locked (note 03). */
  limited?: boolean;
  /* null = ungrouped, and therefore in the implicit bucket at the top */
  groupId: string | null;
}

export interface OutlineGroup {
  id: string;
  name: string;
}

/* Ids prefixed `shape-` / `sticky` are the elements really on the canvas, so
   selection syncs both ways for them. The rest stand in for the parts of a
   bigger board scrolled out of view — a four-element outline would not show
   whether the grouping reads well. */
export const OUTLINE_GROUPS: OutlineGroup[] = [
  { id: "group-discovery", name: "Discovery phase" },
  { id: "group-sketches", name: "Solution sketches" },
  { id: "group-questions", name: "Open questions" },
];

export const OUTLINE_ELEMENTS: OutlineElement[] = [
  {
    id: "sticky",
    name: "Interview five customers",
    type: "postit",
    connections: 2,
    groupId: "group-discovery",
  },
  {
    id: "shape-rounded",
    name: "Problem statement",
    type: "roundedRectangle",
    connections: 3,
    groupId: "group-discovery",
  },
  {
    id: "outline-freehand",
    name: null,
    type: "freehand",
    groupId: "group-discovery",
  },
  {
    id: "shape-circle",
    name: "Success metric",
    type: "circle",
    locked: true,
    connections: 1,
    groupId: "group-discovery",
  },
  {
    id: "outline-sketch-link",
    name: "Competitor teardown",
    type: "link",
    groupId: "group-sketches",
  },
  {
    id: "outline-sketch-file",
    name: "Wireframes v3.pdf",
    type: "file",
    connections: 1,
    groupId: "group-sketches",
  },
  {
    id: "outline-sketch-text",
    name: null,
    type: "text",
    groupId: "group-sketches",
  },
  /* A deliberately long name, so the truncation and the right-edge behaviour of
     the signifiers and the counter are visible in the story. */
  {
    id: "outline-parking",
    name: "Parking lot for everything we could not agree on today",
    type: "postit",
    connections: 4,
    locked: true,
    groupId: null,
  },
  {
    id: "shape-triangle",
    name: null,
    type: "triangle",
    groupId: null,
  },
  {
    id: "outline-q3",
    name: "Revisit in Q3",
    type: "text",
    connections: 12,
    groupId: null,
  },
];

/* Min is the point below which a group name plus its counter stops being
   readable; max follows the common side-panel rule of thumb of 3x the min, past
   which the panel competes with the canvas for the screen rather than annotating
   it. The default sits just above the min — panels of this sort open narrow and
   are widened on demand, not the other way round. */
export const OUTLINE_MIN_WIDTH = 260;
export const OUTLINE_MAX_WIDTH = OUTLINE_MIN_WIDTH * 3;
export const OUTLINE_WIDTH = 320;
export const UNGROUPED = "__ungrouped__";

export const StyledOutlineSidebar = styled.aside`
  flex-shrink: 0;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--selectable);
  background-color: var(--page-paper-main);
  /* raw text (counters) would otherwise fall back to the browser serif,
     since this Storybook ships no global reset */
  font-family: -apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue",
    Arial, sans-serif;
`;

/* Grab strip on the RIGHT edge, because the panel is docked left — dragging it
   right widens. It sits outside the row list and uses mouse events rather than
   HTML5 drag, so it can never be mistaken for a row reorder. 3px wide with a
   larger invisible hit area is the usual compromise: easy to grab. The border
   line itself stays the selectable grey until hover paints the active token
   over it. */
export const StyledOutlineResizer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: -4px;
  width: 8px;
  cursor: col-resize;
  z-index: 2;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    margin-left: -0.5px;
    width: 1px;
    background-color: var(--selectable-active);
    opacity: 0;
    transition: opacity 150ms ease;
  }

  &:hover::after,
  &.resizing::after {
    opacity: 1;
  }
`;

export const StyledOutlineBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 6px 0 24px;
`;

/* Rows are bordered, not carded — the outline is a dense list and rounded
   tiles at this density read as clutter. */
/* Row geometry is My Work's, not something invented for this sidebar: the app's
   `ListItem` is `tw-w-full tw-flex tw-items-center tw-h-10` — a fixed 40px row —
   with `border-bottom: 1px solid var(--color-theme-300)` from `.list-item-wrapper`
   and `background-color: var(--color-theme-200)` on `.task-list-item:hover`.
   Reusing those exact values is what makes the outline read as an ActiveCollab
   list rather than as a bespoke panel. */
export const StyledOutlineRow = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  box-sizing: border-box;
  padding-right: 8px;
  cursor: pointer;
  position: relative;
  border-bottom: 1px solid var(--color-theme-300);

  &:hover {
    background-color: var(--color-theme-200);
  }

  &.selected {
    background-color: var(--color-theme-200);
    box-shadow: inset 2px 0 0 0 ${SELECTION_BLUE};
  }

  /* The drop indicator is drawn ON the row rather than inserted as a sibling
     node, so showing it can never reflow the list mid-drag. */
  &.drop-before::after,
  &.drop-after::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    background-color: ${SELECTION_BLUE};
    z-index: 1;
  }

  &.drop-before::after {
    top: -1px;
  }

  &.drop-after::after {
    bottom: -1px;
  }

  /* Dropping INTO a group targets the whole header, so it highlights rather
     than showing an insertion line. */
  &.drop-into {
    background-color: var(--color-theme-200);
    box-shadow: inset 0 0 0 1px ${SELECTION_BLUE};
  }

  &.dragging {
    opacity: 0.4;
  }

  /* The name is the ONLY flexible cell (flex 0 1 auto plus min-width 0), so it
     sits at its natural width while short — the count and the signifiers then
     read as belonging to it, immediately after it, the way My Work does — and
     ellipsizes once the row runs out of room, at which point whatever follows
     it comes to rest against the right edge. */
  .outline-trailing {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  /* Pushes the options menu to the right edge without a spacer node. */
  .outline-options {
    margin-left: auto;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding-left: 4px;
  }

  /* Hover-only reveal, the house convention: the .task-list-item:hover
     .options-target-menu-show rule in list-view.less, .show-on-hover in the
     ListView story. The .open class keeps the trigger visible while its menu is
     up, so it does not fade out from under the pointer. */
  .outline-hover {
    opacity: 0;
    transition: opacity 200ms ease;
  }

  &:hover .outline-hover,
  .outline-hover.open,
  .outline-hover:focus-within {
    opacity: 1;
  }
`;

/* 8px so the 24px toggle sits in the same left rail as the app's ExpandToggle,
   scaled down from My Work's 24px page margin for a 300px panel. */
export const StyledOutlineGroupRow = styled(StyledOutlineRow)`
  padding-left: 8px;
`;

/* 32px = where the group's 24px toggle ends. The child's type icon starts
   exactly there, putting the child NAME one 16px step right of the group name —
   enough to read as nested without a staircase, which is all one level of
   nesting ever needs. */
export const StyledOutlineChildRow = styled(StyledOutlineRow)`
  padding-left: 32px;
`;

/* Matches the app's ExpandToggle exactly: CollapseExpandSingleIcon at its
   intrinsic 24px, rotate(0) expanded and rotate(180deg) collapsed, over
   `transition-transform` 200ms. */
export const StyledOutlineToggle = styled.button`
  border: 0;
  padding: 0;
  margin: 0 8px 0 0;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: transform 200ms ease;

  &.collapsed {
    transform: rotate(180deg);
  }
`;

/* Same treatment as the count badges on the System Settings pages — a Chip on
   var(--color-theme-300) with var(--color-theme-900) text — at the 18px small
   size, which sits inside a 40px row without crowding it. Wrapped so the 8px
   left margin is one decision rather than repeated at each call site: it is what
   keeps the chip a fixed distance from the name whether the name is an
   EditableText (whose input carries its own 4px padding) or plain Typography. */
export const StyledOutlineCounter = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 8px;
`;

export const OutlineCounter = ({ count }: { count: number }): ReactElement => (
  <StyledOutlineCounter className="outline-trailing">
    <Chip
      label={String(count)}
      backgroundColor="var(--color-theme-300)"
      color="var(--color-theme-900)"
      size="small"
    />
  </StyledOutlineCounter>
);

/* The 2px separator dot is a house convention rather than part of the DS
   Signifier: `_components.task-signifiers.less` puts it on `.c-signifier:before`
   at 2px square, `var(--color-theme-600)`, `margin: 0 6px`, with no
   `:not(:first-child)` guard — so the first signifier gets a leading dot too,
   which is exactly what separates it from the name. */
export const StyledOutlineSignifiers = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  .c-signifier::before {
    content: "";
    flex-shrink: 0;
    width: 2px;
    height: 2px;
    margin: 0 6px;
    border-radius: 100%;
    background-color: var(--color-theme-600);
  }
`;

/* The DS EditableText is an always-live input styled as text — it reveals its
   border on hover and focus rather than swapping into an edit mode. That makes
   it a click-to-edit target, which competes with the row's own click. Element
   rows therefore keep it `disabled` until the row is SELECTED: the first click
   selects, and only then does the name accept a caret. Group rows have no
   selected state, so their name is always live (matching the task-list header
   in ListView) and the toggle, count and row padding carry the collapse. */
/* 24px reserved so the icon column is stable whether or not a type icon is a
   perfect 16px square, and so it lines up with the group toggle above it. */
export const StyledOutlineTypeIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 24px;
  flex-shrink: 0;
`;

export const StyledOutlineName = styled.div`
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;

  /* EditableText's wrapper is an inline-block sized by a hidden measuring span.
     max-width alone is what makes it ellipsize: it caps the wrapper at the
     shrunk cell while leaving the flex basis as the text's own width, so a SHORT
     name still only takes the room it needs and the count stays tucked against
     it. Adding width: 100% here would make every name greedy and push the count
     to the right edge on every row. */
  > div {
    max-width: 100%;
  }

  /* The Ungrouped label is plain Typography, not an EditableText, so it needs
     its own truncation — and the same 4px side padding EditableText's input
     carries. Without that padding its box ends 4px earlier than an editable
     group name's, which is exactly why the Ungrouped counter sat tighter to its
     label than every other row's did. */
  > .typography {
    display: block;
    padding-left: 4px;
    padding-right: 4px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  /* The last selector is for rows whose label is plain Typography rather than an
     EditableText — an entity card's inherited title, and the Limited Access
     label. They get the same muted, italic placeholder treatment. */
  &.unnamed .presentation,
  &.unnamed input,
  &.unnamed > .typography {
    font-style: italic;
    color: var(--color-theme-500);
  }
`;

/* A limited row is labelled before its type is consulted, because the type
   label would say "Task" or "Project" — and which KIND of record is hidden is
   itself something the viewer is not entitled to. The card face and the
   Inspector header withhold it too. */
export const outlineLabel = (element: OutlineElement): string => {
  if (element.limited) {
    return LIMITED_ACCESS_LABEL;
  }

  return element.name ?? OUTLINE_TYPE_LABELS[element.type];
};

/**
 * Outline rows for the entity cards on the canvas.
 *
 * They are DERIVED on every render rather than seeded into the outline's own
 * state the way the mock elements are. That is the whole trick that keeps this
 * cheap: the canvas already owns these elements, so there is no second copy to
 * reconcile when one is added, resolved or cancelled, and the drag-reorder and
 * grouping state the outline holds for its mock rows is left completely alone.
 *
 * The row id IS the canvas element id, which is what makes selection sync free
 * in both directions — the sidebar and the canvas already share `selectedId`.
 */
export const entityOutlineRows = (
  elements: ProjectElementDef[],
  locks: Record<string, boolean>
): OutlineElement[] =>
  elements.map((element) => ({
    id: element.id,
    /* No name for a limited card — nothing about the entity exists to show. */
    name: element.limited ? null : projectElementTitle(element),
    type: element.kind,
    renamable: false,
    limited: element.limited === true,
    /* A limited card is locked for this viewer, always. Everything else follows
       the same lock flag the Inspector's checkbox writes. */
    locked: element.limited === true || (locks[element.id] ?? false),
    groupId: null,
  }));

/* Both menus follow the shared TaskListOptionsMenu shape: Menu in normal mode
   (260px bubble), an IconButton + TreeDots trigger whose `.open` class keeps it
   visible while the menu is up, and a flat List of leading-icon ListItems
   grouped by ListSeparator. Every row is an icon plus a caption — no submenu
   rows and no icon-only action strip, so there is exactly one way to read the
   menu.

   Note where STACKING lives: bring to front / send to back are here, not in the
   outline. The outline orders the document; the menu orders the pixels. Keeping
   them apart is the whole reason the sidebar can be an outline rather than a
   layers panel. */
export const OUTLINE_MENU_LIST_STYLE = { padding: "8px 0" };
export const OUTLINE_MENU_WIDTH = 264;

/* Same drill-in mechanics as the board Settings menu: the paper clips the two
   sliding panels and gives them a positioning context. */
export const OutlineMenuSliderStyle = createGlobalStyle`
  .c-simple-menu__paper.outline-element-menu {
    overflow: hidden;
    position: relative;
  }
`;

export const StyledOutlineElementMenu = styled.div`
  .drill-item {
    justify-content: space-between;
  }

  .drill-item .opt-left {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  /* ListItem spaces its leading icon with a DIRECT-child rule
     (> svg:first-child { margin-right: 8px }). Arrange's icon sits one level
     down inside .opt-left so the caret can be pushed to the far right, which
     puts it outside that selector — hence its caption sat 8px left of every
     other item's. Restate the same value rather than restructuring the row. */
  .drill-item .opt-left > svg:first-child {
    margin-right: 8px;
  }

  .drill-item .drill-caret {
    flex-shrink: 0;
    fill: var(--color-theme-600);
  }
`;

export const ARRANGE_ITEMS: { label: string; Icon: OutlineIcon }[] = [
  { label: "Bring to Front", Icon: LayerFrontIcon },
  { label: "Send Forward", Icon: LayerForwardIcon },
  { label: "Send Backward", Icon: LayerBackwardIcon },
  { label: "Send to Back", Icon: LayerBackIcon },
];

export interface OutlineElementOptionsMenuProps {
  locked: boolean;
  onToggleLock: () => void;
  /* Entity cards inherit their name, so the menu drops its Rename row for them
     rather than offering an action that cannot apply. Defaults to true. */
  renamable?: boolean;
}

export const OutlineElementOptionsMenu = ({
  locked,
  onToggleLock,
  renamable = true,
}: OutlineElementOptionsMenuProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"index" | "arrange">("index");
  const [isLeft, setIsLeft] = useState(true);
  const [height, setHeight] = useState(0);

  const measure = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setHeight(node.offsetHeight);
    }
  }, []);

  const goToArrange = (): void => {
    setView("arrange");
    setIsLeft(true);
  };

  const backToIndex = (): void => {
    setView("index");
    setIsLeft(false);
  };

  const handleClose = (): void => {
    setOpen(false);
    setView("index");
    setIsLeft(true);
  };

  const indexView = (
    <StyledOutlineElementMenu>
      <List style={OUTLINE_MENU_LIST_STYLE} tabIndex={-1}>
        {renamable ? (
          <ListItem>
            <EditIcon />
            Rename
          </ListItem>
        ) : null}
        <ListItem>
          <DuplicateIcon />
          Duplicate
        </ListItem>
        <ListSeparator />
        <ListItem>
          <FolderMoveIcon />
          Move to Group
        </ListItem>
        {/* Stacking drills in rather than sitting flat, so the four z-order
            actions cannot be mistaken for outline ordering. */}
        <ListItem className="drill-item" onClick={goToArrange}>
          <span className="opt-left">
            <LayersIcon />
            Arrange
          </span>
          <CaretRightIcon className="drill-caret" width={16} height={16} />
        </ListItem>
        <ListSeparator />
        {/* Really toggles the row's lock signifier, so the menu and the row
            cannot disagree about state — the point of wiring it up rather than
            leaving it a no-op like the rest. */}
        <ListItem
          onClick={() => {
            onToggleLock();
            handleClose();
          }}
        >
          <LockIcon />
          {locked ? "Unlock" : "Lock"}
        </ListItem>
        <ListSeparator />
        <ListItem>
          <TrashIcon />
          Move to Trash
        </ListItem>
      </List>
    </StyledOutlineElementMenu>
  );

  const arrangeView = (
    <StyledOutlineElementMenu>
      <MenuHeader
        title="Arrange"
        leftElement={
          <IconButton
            type="button"
            variant="text gray"
            size="small"
            onClick={backToIndex}
            aria-label="Back"
          >
            <ArrowLeftIcon />
          </IconButton>
        }
      />
      <List style={OUTLINE_MENU_LIST_STYLE} tabIndex={-1}>
        {ARRANGE_ITEMS.map(({ label, Icon }) => (
          <ListItem key={label} onClick={backToIndex}>
            <Icon />
            {label}
          </ListItem>
        ))}
      </List>
    </StyledOutlineElementMenu>
  );

  return (
    <>
      <OutlineMenuSliderStyle />
      <Menu
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        position="bottom-end"
        menuClassName="outline-element-menu"
        target={
          <IconButton
            type="button"
            variant="text gray"
            className={open ? "open" : undefined}
            active={open}
          >
            <TreeDotsIcon />
          </IconButton>
        }
      >
        <ResizeTransition in style={{ height, width: OUTLINE_MENU_WIDTH }}>
          <div style={{ position: "relative", overflow: "hidden" }}>
            <SlideLeftRightTransition
              in={view === "index"}
              direction={isLeft ? "left" : "right"}
            >
              <div ref={measure} style={{ width: OUTLINE_MENU_WIDTH }}>
                {indexView}
              </div>
            </SlideLeftRightTransition>
            <SlideLeftRightTransition
              in={view === "arrange"}
              direction={isLeft ? "left" : "right"}
            >
              <div ref={measure} style={{ width: OUTLINE_MENU_WIDTH }}>
                {arrangeView}
              </div>
            </SlideLeftRightTransition>
          </div>
        </ResizeTransition>
      </Menu>
    </>
  );
};

export const OutlineGroupOptionsMenu = (): ReactElement => {
  const [open, setOpen] = useState(false);

  return (
    <Menu
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      position="bottom-end"
      target={
        <IconButton
          type="button"
          variant="text gray"
          className={open ? "open" : undefined}
          active={open}
        >
          <TreeDotsIcon />
        </IconButton>
      }
    >
      <List style={OUTLINE_MENU_LIST_STYLE} tabIndex={-1}>
        <ListItem>
          <EditIcon />
          Rename
        </ListItem>
        <ListItem>
          <DuplicateIcon />
          Duplicate
        </ListItem>
        <ListSeparator />
        <ListItem>
          <FolderMoveIcon />
          Ungroup
        </ListItem>
        <ListSeparator />
        <ListItem>
          <TrashIcon />
          Move to Trash
        </ListItem>
      </List>
    </Menu>
  );
};

export type DropSpot =
  | { kind: "before" | "after"; id: string }
  | { kind: "into"; id: string };

export type LeftPanel = "info" | "outline";

export const INFO_ROWS: [string, string][] = [
  ["Owner", "Ilija Studen"],
  ["Created", "Jul 30, 2026"],
  ["Members", "12"],
  ["Elements", "48"],
  ["Last edited", "2 hours ago"],
];

/* The upper-left menu — exit fullscreen, board name, the Show / Hide Sidebar
   toggle and the people menu. It renders in one of two places: floating over the
   board while the sidebar is closed, and inside the sidebar's header once it
   opens, so the controls follow the panel rather than being shoved aside by it. */
export const LeftMenuContents = ({
  sidebarOpen,
  onToggleSidebar,
  boardName,
  onRenameBoard,
}: {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  boardName: string;
  onRenameBoard: (name: string) => void;
}): ReactElement => (
  <>
    <Tooltip title="Back to Whiteboards">
      <IconButton variant="text gray">
        <ArrowLeftIcon />
      </IconButton>
    </Tooltip>
    <span className="wb-title">
      <EditableText
        value={boardName}
        variant="Title 2"
        weight="bold"
        onSave={(event) => onRenameBoard(event.target.value)}
        inputProps={{ maxLength: 150 }}
      />
    </span>
    {/* One control for the whole left dock — no separate Info / Outline icons,
        and no active state, since the panel being on screen is its own tell. */}
    <Tooltip title={sidebarOpen ? "Hide Sidebar" : "Show Sidebar"}>
      <IconButton variant="text gray" onClick={onToggleSidebar}>
        <SidebarLeftIcon />
      </IconButton>
    </Tooltip>
    <WhiteboardPeopleMenu />
  </>
);

/* When the sidebar is open the left menu becomes its header: same controls, but
   laid out as a flat 60px bar with the board name flexing to fill the middle. */
export const StyledSidebarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  height: 60px;
  padding: 0 12px;
  box-sizing: border-box;
  flex-shrink: 0;

  .wb-title {
    flex: 1;
    min-width: 0;
    margin: 0 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/* Centered segmented switch between the dock's two panels; the active one is
   highlighted. This row replaces the old per-panel header. */
export const StyledChooseRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 16px;
  flex-shrink: 0;
`;

/* The app renders the switch through ChooseV2 in a fixed 224px slot with the
   button group centered inside it (the group alone is narrower than the slot,
   and ChooseV2's own flex container leaves it at the left edge). */
export const StyledPanelChooseSlot = styled.div`
  width: 224px;

  .c-btn-group {
    margin: 0 auto;
  }
`;

export const StyledInfoPanel = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .info-meta {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .info-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }
`;

/* Mock only — the Info panel's content is a placeholder while the two-panel dock
   is explored; the fields here are not the point. */
export const InfoPanelContent = ({
  boardName,
}: {
  boardName: string;
}): ReactElement => (
  <StyledInfoPanel>
    <Header3 color="primary">{boardName}</Header3>
    <div className="info-meta">
      {INFO_ROWS.map(([label, value]) => (
        <div className="info-row" key={label}>
          <Caption1 color="secondary">{label}</Caption1>
          <Body2 weight="medium">{value}</Body2>
        </div>
      ))}
    </div>
  </StyledInfoPanel>
);

/* Centered text button below the group list — same text-gray treatment as the
   old header icon (no outline, gray fill on hover), now with a label since it is
   no longer squeezed into a header. */
export const StyledNewGroupRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 0 4px;

  svg {
    width: 16px;
    height: 16px;
    margin-right: 6px;
  }
`;

export interface LeftSidebarProps {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  activePanel: LeftPanel;
  onPanelChange: (panel: LeftPanel) => void;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  boardName: string;
  onRenameBoard: (name: string) => void;
  /* The live entity cards from the canvas. The outline derives rows from them
     instead of owning them (see `entityOutlineRows`), so they stay in one place
     and the mock's own model is untouched. */
  projectElements: ProjectElementDef[];
  /* The board's shared per-element lock flag — the same map the Inspector's
     Locked checkbox writes, so a row's lock signifier and the Inspector can
     never disagree. */
  locks: Record<string, boolean>;
  onToggleElementLock: (id: string) => void;
}

export const LeftSidebar = ({
  selectedId,
  onSelect,
  activePanel,
  onPanelChange,
  sidebarOpen,
  onToggleSidebar,
  boardName,
  onRenameBoard,
  projectElements,
  locks,
  onToggleElementLock,
}: LeftSidebarProps): ReactElement => {
  const [width, setWidth] = useState(OUTLINE_WIDTH);
  const [resizing, setResizing] = useState(false);
  const [groups, setGroups] = useState<OutlineGroup[]>(OUTLINE_GROUPS);
  const [elements, setElements] = useState<OutlineElement[]>(OUTLINE_ELEMENTS);
  const [collapsed, setCollapsed] = useState<string[]>(["group-sketches"]);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropSpot, setDropSpot] = useState<DropSpot | null>(null);

  const isGroup = useCallback(
    (id: string) => groups.some((group) => group.id === id),
    [groups]
  );

  const membersOf = useCallback(
    (groupId: string | null) =>
      elements.filter((element) => element.groupId === groupId),
    [elements]
  );

  /* Derived, never stored. Entity rows join the Ungrouped bucket — the section
     the outline itself describes as "where everything starts out and where new
     work lands" — and they join it at the END, after the mock rows, so the
     hand-ordered list a designer is looking at does not shuffle when a card is
     added to the board. */
  const entityRows = entityOutlineRows(projectElements, locks);

  const toggleCollapsed = useCallback((id: string) => {
    setCollapsed((current) =>
      current.includes(id)
        ? current.filter((entry) => entry !== id)
        : [...current, id]
    );
  }, []);

  const renameGroup = useCallback((id: string, name: string) => {
    setGroups((current) =>
      current.map((group) => (group.id === id ? { ...group, name } : group))
    );
  }, []);

  const renameElement = useCallback((id: string, name: string) => {
    setElements((current) =>
      current.map((element) =>
        element.id === id ? { ...element, name } : element
      )
    );
  }, []);

  const toggleLock = useCallback((id: string) => {
    setElements((current) =>
      current.map((element) =>
        element.id === id ? { ...element, locked: !element.locked } : element
      )
    );
  }, []);

  /* The gesture is tracked from its ORIGIN (pointer x + width at mousedown)
     rather than from the pointer's absolute position, so the panel edge stays
     locked to the cursor even once the width clamps at a bound — otherwise the
     pointer drifts away from the edge and the panel jumps when it comes back.
     Listeners go on window so the drag survives the pointer leaving the strip,
     and body user-select is suspended so dragging does not select the rows. */
  const startResize = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = event.currentTarget.parentElement?.offsetWidth ?? 0;
    const previousUserSelect = document.body.style.userSelect;

    setResizing(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    const handleMove = (moveEvent: MouseEvent): void => {
      const next = startWidth + (moveEvent.clientX - startX);
      setWidth(Math.min(OUTLINE_MAX_WIDTH, Math.max(OUTLINE_MIN_WIDTH, next)));
    };

    const handleUp = (): void => {
      setResizing(false);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  }, []);

  const addGroup = useCallback(() => {
    setGroups((current) => [
      ...current,
      { id: `group-${Date.now()}`, name: "New group" },
    ]);
  }, []);

  /* Reorder mirrors the server contract: splice the moved entry into its new
     slot, then treat the resulting array order AS the 1..N renumbering the
     server would write back. Groups reorder among groups; an element moves
     within or between containers, and its groupId follows the drop target —
     which is the whole of "one level of nesting" as an invariant, since a
     group is never a valid child. */
  const handleDrop = useCallback(() => {
    const draggedId = dragId;
    const spot = dropSpot;

    setDragId(null);
    setDropSpot(null);

    if (!draggedId || !spot || draggedId === spot.id) {
      return;
    }

    if (isGroup(draggedId)) {
      /* a group can only land among groups, never inside one */
      if (spot.kind === "into" || !isGroup(spot.id)) {
        return;
      }

      setGroups((current) => {
        const from = current.findIndex((group) => group.id === draggedId);
        const next = [...current];
        const [moved] = next.splice(from, 1);
        const to = next.findIndex((group) => group.id === spot.id);

        next.splice(spot.kind === "before" ? to : to + 1, 0, moved);

        return next;
      });

      return;
    }

    setElements((current) => {
      const from = current.findIndex((element) => element.id === draggedId);
      const next = [...current];
      const [moved] = next.splice(from, 1);

      if (spot.kind === "into") {
        const groupId = spot.id === UNGROUPED ? null : spot.id;
        const last = next.reduce(
          (acc, element, index) => (element.groupId === groupId ? index : acc),
          -1
        );

        next.splice(last + 1, 0, { ...moved, groupId });

        return next;
      }

      const target = next.find((element) => element.id === spot.id);

      if (!target) {
        return current;
      }

      const to = next.findIndex((element) => element.id === spot.id);

      next.splice(spot.kind === "before" ? to : to + 1, 0, {
        ...moved,
        groupId: target.groupId,
      });

      return next;
    });
  }, [dragId, dropSpot, isGroup]);

  const rowDragProps = useCallback(
    (id: string) => ({
      draggable: true,
      onDragStart: (event: React.DragEvent) => {
        /* A live EditableText input sits inside the row, and dragging a text
           selection inside it must not turn into a row reorder. */
        if ((event.target as HTMLElement).closest("input")) {
          event.preventDefault();

          return;
        }

        event.stopPropagation();
        setDragId(id);
      },
      onDragEnd: () => {
        setDragId(null);
        setDropSpot(null);
      },
      onDragOver: (event: React.DragEvent) => {
        if (!dragId || dragId === id) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const box = event.currentTarget.getBoundingClientRect();
        const isTopHalf = event.clientY - box.top < box.height / 2;

        /* an element dragged onto a GROUP header joins that group; the
           insertion halves are reserved for reordering groups themselves */
        if (isGroup(id) && !isGroup(dragId)) {
          setDropSpot({ kind: "into", id });

          return;
        }

        setDropSpot({ kind: isTopHalf ? "before" : "after", id });
      },
      onDrop: (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        handleDrop();
      },
    }),
    [dragId, handleDrop, isGroup]
  );

  const dropClass = useCallback(
    (id: string) => {
      if (dragId === id) {
        return "dragging";
      }

      if (!dropSpot || dropSpot.id !== id) {
        return "";
      }

      return `drop-${dropSpot.kind}`;
    },
    [dragId, dropSpot]
  );

  const renderElementRow = (element: OutlineElement): ReactElement => {
    /* A limited row takes the neutral lock glyph, not its type glyph: the type
       glyph would say whether a task or a project is hidden. */
    const TypeIcon = element.limited
      ? LockIcon
      : OUTLINE_TYPE_ICONS[element.type];
    const selected = element.id === selectedId;
    const renamable = element.renamable !== false;
    /* Muted + italic covers both "never named" and "not yours to see". */
    const muted = element.limited === true || !element.name;

    return (
      <StyledOutlineChildRow
        key={element.id}
        className={`${selected ? "selected" : ""} ${dropClass(element.id)}`}
        onClick={() => onSelect(element.id)}
        /* Entity rows are derived, so they are deliberately NOT draggable: they
           have no place in the mock's ordered model, and dropping one into a
           group would be an edit with nowhere to land. Grouping and reorder for
           every other row are unchanged. */
        {...(renamable ? rowDragProps(element.id) : {})}
      >
        <StyledOutlineTypeIcon>
          <TypeIcon
            width={16}
            height={16}
            fill={muted ? "var(--color-theme-500)" : "var(--color-theme-700)"}
          />
        </StyledOutlineTypeIcon>
        <StyledOutlineName className={muted ? "unnamed" : ""}>
          {renamable ? (
            <EditableText
              value={outlineLabel(element)}
              variant="Body 2"
              weight="medium"
              disabled={!selected}
              onSave={(event) => renameElement(element.id, event.target.value)}
              inputProps={{ maxLength: 150 }}
            />
          ) : (
            /* Plain Typography, so there is no input to click into at all —
               the name belongs to the linked entity. */
            <Body2 weight="medium">{outlineLabel(element)}</Body2>
          )}
        </StyledOutlineName>
        {element.locked || element.connections ? (
          <StyledOutlineSignifiers className="outline-trailing">
            {element.connections ? (
              <Signifier
                type={DependencySmallIcon}
                value={element.connections}
                tooltipText={`${element.connections} ${
                  element.connections === 1 ? "connection" : "connections"
                }`}
              />
            ) : null}
            {element.locked ? (
              <Signifier type={LockSmallIcon} tooltipText="Locked" />
            ) : null}
          </StyledOutlineSignifiers>
        ) : null}
        {/* A limited row carries no menu at all: every action in it — rename,
            duplicate, arrange, lock, trash — is something this viewer may not
            do to an element they cannot see (note 03). */}
        {element.limited ? null : (
          <span className="outline-options outline-hover">
            <OutlineElementOptionsMenu
              renamable={renamable}
              locked={Boolean(element.locked)}
              /* Mock rows keep their own flag; an entity row's lock is the
                 board's, so it goes to the shared map the Inspector uses. */
              onToggleLock={() =>
                renamable
                  ? toggleLock(element.id)
                  : onToggleElementLock(element.id)
              }
            />
          </span>
        )}
      </StyledOutlineChildRow>
    );
  };

  return (
    <StyledOutlineSidebar style={{ width }}>
      <StyledOutlineResizer
        className={resizing ? "resizing" : ""}
        onMouseDown={startResize}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize outline"
      />

      {/* The upper-left menu, relocated into the panel header once it is open. */}
      <StyledSidebarHeader>
        <LeftMenuContents
          sidebarOpen={sidebarOpen}
          onToggleSidebar={onToggleSidebar}
          boardName={boardName}
          onRenameBoard={onRenameBoard}
        />
      </StyledSidebarHeader>

      <StyledChooseRow>
        <StyledPanelChooseSlot>
          <ChooseV2
            required
            options={[
              { id: "info", name: "Info" },
              { id: "outline", name: "Outline" },
            ]}
            selected={[activePanel]}
            onChange={(selected) => onPanelChange(selected[0] as LeftPanel)}
          />
        </StyledPanelChooseSlot>
      </StyledChooseRow>

      {activePanel === "info" ? (
        <InfoPanelContent boardName={boardName} />
      ) : null}

      {activePanel === "outline" ? (
        <StyledOutlineBody>
          {/* Ungrouped leads the list rather than trailing it. An element only
            reaches a group deliberately, so the bucket is where everything
            starts out and where new work lands — burying it under every group
            would hide the one section that is always changing. It is still NOT
            an entity: no rename, no reorder, no options menu. Which means it can
            never carry a heading into an exported document, so anything worth
            exporting has to be moved into a real group first. */}
          <StyledOutlineGroupRow
            className={
              dropSpot?.kind === "into" && dropSpot.id === UNGROUPED
                ? "drop-into"
                : ""
            }
            onClick={() => toggleCollapsed(UNGROUPED)}
            onDragOver={(event) => {
              if (!dragId || isGroup(dragId)) {
                return;
              }

              event.preventDefault();
              setDropSpot({ kind: "into", id: UNGROUPED });
            }}
            onDrop={(event) => {
              event.preventDefault();
              handleDrop();
            }}
          >
            <StyledOutlineToggle
              type="button"
              className={collapsed.includes(UNGROUPED) ? "collapsed" : ""}
              aria-label="Toggle ungrouped"
              onClick={(event) => {
                event.stopPropagation();
                toggleCollapsed(UNGROUPED);
              }}
            >
              <CollapseExpandSingleIcon fill="var(--color-theme-700)" />
            </StyledOutlineToggle>
            <StyledOutlineName>
              <Header3 color="primary">Ungrouped</Header3>
            </StyledOutlineName>
            <OutlineCounter
              count={membersOf(null).length + entityRows.length}
            />
          </StyledOutlineGroupRow>

          {collapsed.includes(UNGROUPED)
            ? null
            : [...membersOf(null), ...entityRows].map(renderElementRow)}

          {groups.map((group) => {
            const members = membersOf(group.id);
            const isOpen = !collapsed.includes(group.id);

            return (
              <React.Fragment key={group.id}>
                <StyledOutlineGroupRow
                  className={dropClass(group.id)}
                  onClick={() => toggleCollapsed(group.id)}
                  {...rowDragProps(group.id)}
                >
                  <StyledOutlineToggle
                    type="button"
                    className={isOpen ? "" : "collapsed"}
                    aria-label={isOpen ? "Collapse group" : "Expand group"}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleCollapsed(group.id);
                    }}
                  >
                    <CollapseExpandSingleIcon fill="var(--color-theme-700)" />
                  </StyledOutlineToggle>
                  <StyledOutlineName
                    onClick={(event) => event.stopPropagation()}
                  >
                    <EditableText
                      value={group.name}
                      variant="Header 3"
                      weight="bold"
                      onSave={(event) =>
                        renameGroup(group.id, event.target.value)
                      }
                      inputProps={{ maxLength: 150 }}
                    />
                  </StyledOutlineName>
                  <OutlineCounter count={members.length} />
                  <span className="outline-options outline-hover">
                    <OutlineGroupOptionsMenu />
                  </span>
                </StyledOutlineGroupRow>

                {isOpen ? members.map(renderElementRow) : null}
              </React.Fragment>
            );
          })}

          <StyledNewGroupRow>
            <Button variant="text gray" onClick={addGroup}>
              <FolderPlusIcon />
              New Group
            </Button>
          </StyledNewGroupRow>
        </StyledOutlineBody>
      ) : null}
    </StyledOutlineSidebar>
  );
};
